<div class="flex flex-col w/full bg-white rounded-lg p-6 shadow w-full">
  <div class="flex flex-col w-full">
    <h2 class="font-bold mat-title">
      Dealership Letterhead
      <span *ngIf="primaryRooftopId" class="text-sm font-normal text-gray-500 ml-2">
        — used by Rooftop {{ primaryRooftopId }} (default fallback for other rooftops)
      </span>
    </h2>
    <div class="flex flex-col px-2 py-2">
      <!-- Before upload -->
      <div *ngIf="!uploadedImage" class="flex flex-col md:flex-row items-start place-content-between gap-6 rounded-md">
        <div
          class="flex w-1/2 items-center justify-center bg-grey-100 border border-dashed border-grey-300 h-40 text-grey-500 text-sm rounded-md">
          No letterhead uploaded
        </div>
        <div class="flex flex-col gap-2 w-1/2">
          <div class="flex items-center gap-6">
            <input type="file" id="fileInput" (change)="onFileSelected($event)" accept=".png,.jpg,.jpeg,.svg"
              class="hidden">
            <button type="button" (click)="triggerFileInput()"
              class="px-4 py-2 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50">Choose File</button>
            <span class="text-gray-500 text-sm">{{ selectedFileName || 'No file selected' }}</span>
          </div>
          <div class="text-sm text-gray-400 leading-6">
            <p>Supported formats: SVG, PNG, JPEG</p>
            <p>Aspect ratio: 1:1 to 3:2</p>
            <p>Max file size: 2MB</p>
          </div>
        </div>
      </div>




      <!-- After upload -->
      <div *ngIf="uploadedImage" class="flex flex-col md:flex-row items-start gap-4 rounded-md mt-2">
        <img [src]="uploadedImage" alt="Dealer Letterhead Preview"
          class="w-1/2 h-40 object-contain bg-white border border-grey-200 rounded-md" />
        <div class="flex flex-col gap-2 text-sm">
          <button (click)="removeImage()"
            class="self-start px-3 py-1 border border-red-500 text-red-600 rounded-md hover:bg-red-50">
            Remove
          </button>
          <p><strong>Filename:</strong> {{ selectedFileName }}</p>
          <p><strong>Size:</strong> {{ selectedFileSize }}</p>
          <p><strong>Uploaded:</strong> {{ uploadDate | date:'MMM d, y' }}</p>
        </div>
      </div>
      <div class="w-full flex justify-center pt-6">
        <button mat-button mat-raised-button color="primary" [disabled]="!selectedFile"
          (click)="updateDealershipLogo()">Update</button>
      </div>
    </div>

    <!-- Per-rooftop letterheads — populated from dealerLogosByRooftop on the settings record.
         Multi-rooftop dealers (mostly multi-OEM groups) can override the dealer-default
         letterhead on a per-rooftop basis. Each card replaces just that rooftop's slot.
         The section always renders so users can add overrides on dealers the migration
         didn't pre-populate (single-rooftop or multi-rooftop-agreeing dealers). -->
    <div *ngIf="settings" class="mt-6">
      <h2 class="font-bold mat-title">Per-Rooftop Letterhead Overrides</h2>
      <p class="text-sm text-gray-500 px-2">
        Each rooftop can use a different letterhead. Rooftops not listed here fall back to the dealership letterhead above.
      </p>
      <div class="flex flex-col px-2 py-2 gap-4">
        <div *ngFor="let rooftopId of rooftopIds"
          class="flex flex-col md:flex-row items-start gap-4 rounded-md border border-grey-200 p-4">
          <div class="flex flex-col w-1/4">
            <p class="font-semibold">Rooftop {{ rooftopId }}</p>
            <p class="text-xs text-gray-400">id: {{ rooftopId }}</p>
          </div>
          <img *ngIf="rooftopLogo(rooftopId).url"
            [src]="rooftopLogo(rooftopId).url"
            alt="Rooftop {{ rooftopId }} Letterhead"
            class="w-1/4 h-32 object-contain bg-white border border-grey-200 rounded-md" />
          <div *ngIf="!rooftopLogo(rooftopId).url"
            class="flex w-1/4 h-32 items-center justify-center bg-grey-100 border border-dashed border-grey-300 text-grey-500 text-sm rounded-md">
            No rooftop letterhead
          </div>
          <div class="flex flex-col gap-2 text-sm flex-1">
            <p *ngIf="rooftopLogo(rooftopId).originalFileName">
              <strong>Filename:</strong> {{ rooftopLogo(rooftopId).originalFileName }}
            </p>
            <p *ngIf="rooftopLogo(rooftopId).fileSize">
              <strong>Size:</strong> {{ rooftopLogo(rooftopId).fileSize }}
            </p>
            <p *ngIf="rooftopLogo(rooftopId).uploadedAt">
              <strong>Uploaded:</strong> {{ rooftopLogo(rooftopId).uploadedAt | date:'MMM d, y' }}
            </p>
            <div class="flex items-center gap-3 mt-2">
              <input type="file"
                id="rooftopFileInput_{{ rooftopId }}"
                (change)="onRooftopFileSelected($event, rooftopId)"
                accept=".png,.jpg,.jpeg,.svg,.bmp,.gif"
                class="hidden">
              <button type="button"
                (click)="triggerRooftopFileInput(rooftopId)"
                [disabled]="uploadingRooftopId === rooftopId"
                class="px-4 py-2 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50 disabled:opacity-50">
                {{ uploadingRooftopId === rooftopId ? 'Uploading…' : 'Replace letterhead' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Add another rooftop — collapsed by default -->
        <ng-container *ngIf="!showAddRooftopForm">
          <button type="button"
            (click)="openAddRooftopForm()"
            class="self-start px-4 py-2 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50">
            + Add another rooftop letterhead
          </button>
        </ng-container>

        <div *ngIf="showAddRooftopForm" class="flex flex-col gap-3 rounded-md border border-dashed border-grey-300 p-4">
          <p class="font-semibold text-sm">Add a letterhead for another rooftop</p>
          <p class="text-xs text-gray-500">
            Enter the rooftop ID and choose a file. The new letterhead will only be used for that specific rooftop.
          </p>
          <div class="flex flex-col md:flex-row items-start md:items-center gap-3">
            <mat-form-field appearance="outline" class="!w-32">
              <mat-label>Rooftop ID</mat-label>
              <input matInput type="number" min="1" [(ngModel)]="newRooftopId" placeholder="e.g. 3" />
            </mat-form-field>
            <input type="file"
              id="newRooftopFileInput"
              (change)="onNewRooftopFileSelected($event)"
              accept=".png,.jpg,.jpeg,.svg,.bmp,.gif"
              class="text-sm">
            <button type="button"
              [disabled]="!canSubmitNewRooftop()"
              (click)="submitNewRooftop()"
              class="px-4 py-2 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50 disabled:opacity-50">
              {{ addingRooftop ? 'Uploading…' : 'Add rooftop letterhead' }}
            </button>
            <button type="button"
              [disabled]="addingRooftop"
              (click)="cancelAddRooftop()"
              class="px-4 py-2 border border-grey-300 text-gray-600 rounded-md hover:bg-grey-50 disabled:opacity-50">
              Cancel
            </button>
          </div>
          <p *ngIf="newRooftopValidationError()" class="text-xs text-red-600">{{ newRooftopValidationError() }}</p>
        </div>
      </div>
    </div>

    <div>
      <h2 class="font-bold mat-title">Marketing Settings</h2>
      <div *ngIf="settings" class="flex flex-col px-2 py-2">
        <form class="flex flex-row flex-wrap justify-between" [formGroup]="settingsForm">
          <mat-form-field appearance="outline" class="settings-input">
            <mat-label>X (Twitter) Handle</mat-label>
            <input placeholder="handle" matInput formControlName="twitterHandle" />
          </mat-form-field>

          <mat-form-field appearance="outline" class="settings-input">
            <mat-label>Facebook Username</mat-label>
            <input placeholder="username" matInput formControlName="facebookHandle" />
          </mat-form-field>

          <mat-form-field appearance="outline" class="settings-input">
            <mat-label>Instagram Username</mat-label>
            <input placeholder="username" matInput formControlName="instagramHandle" />
          </mat-form-field>

          <mat-form-field appearance="outline" class="settings-input">
            <mat-label>Youtube Username</mat-label>
            <input placeholder="username" matInput formControlName="youtubeHandle" />
          </mat-form-field>

          <mat-form-field appearance="outline" class="settings-input">
            <mat-label>Website Url</mat-label>
            <input placeholder="https://www.website.com" matInput formControlName="websiteUrl" />
          </mat-form-field>

        </form>
        <div class="w-full flex justify-center">
          <button mat-button mat-raised-button color="primary"
            (click)="updateSettings(settingsForm.value)">Update</button>
        </div>
      </div>
    </div>
  </div>
</div>